import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  ViewChild,
  ElementRef,
  ContentChildren,
  QueryList,
  Renderer2,
  ChangeDetectorRef,
  EventEmitter
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { ActionComponent } from './action.component';
import { GlobalEventListenerService } from '../../services/global-event-listener.service';
import { filter } from 'rxjs/operators';
import { ActionMenuService } from './action-menu.service';

export const ActionMenuSize = {
  small: 'small',
  normal: 'normal',
  large: 'large'
};

export const ActionMenuLocation = {
  left: 'left',
  right: 'right'
};

@Component({
    selector: 'topo-action-menu',
    templateUrl: './action-menu.component.html',
    styleUrls: ['./action-menu.component.scss'],
    standalone: false
})
export class ActionMenuComponent implements OnInit, OnDestroy {
  @Input() item: any;
  @Input() size = 'normal';
  @Input() location = 'left';
  @Input() appendToBody = false;
  @Input() icon = 'more';
  @Input() theme: 'default' | 'purple' = 'default';
  @Input() skinny = false;
  @Input() disabled = false;
  @Input() alignBottom = false;
  @Input() alignRight = false;
  @Input() submenuLabel: string;
  @Input() onShowMenu: Observable<{ x: number; y: number }> = new Subject<{ x: number; y: number }>();
  @Output() showHideMenu = new EventEmitter<boolean>();
  @ViewChild('menu') menu: ElementRef;
  @ViewChild('dropdownMenu') dropdownMenu: ElementRef;
  @ContentChildren(ActionComponent, { descendants: true }) actions: QueryList<ActionComponent>;
  destroy$: Subject<void> = new Subject();
  isActive = false;
  subscriptions: Subscription[] = [];
  noIconClass = '';
  contentClass = '';
  private lastClickEvent = 0;

  buttonClass: 'button' | 'purple-button' = 'button';
  isGhostClass: 'is-ghost' | '' = 'is-ghost';
  isSmallClass: 'is-small' | '' = 'is-small';
  isTinyClass: 'is-tiny' | '' = '';
  isLargeClass: 'is-large' | '' = '';
  isMediumClass: 'is-medium' | '' = '';

  get isLeft(): boolean {
    return this.location === ActionMenuLocation.left;
  }

  constructor(
    private eventListenerService: GlobalEventListenerService,
    private renderer: Renderer2,
    private changeDetector: ChangeDetectorRef,
    private actionMenuService: ActionMenuService
  ) {}

  ngOnInit(): void {
    // Set classes for input values
    if (this.theme === 'purple') {
      this.buttonClass = 'purple-button';
      this.isGhostClass = '';
      this.isSmallClass = '';
    }
    this.noIconClass = !this.icon ? 'no-icon' : '';
    this.isTinyClass = this.size === 'small' ? 'is-tiny' : '';
    this.isSmallClass = this.size === 'normal' ? 'is-small' : '';
    this.isMediumClass = this.size === 'medium' ? 'is-medium' : '';
    this.isLargeClass = this.size === 'large' ? 'is-large' : '';
    this.contentClass = this.skinny ? 'skinny' : '';

    // listen for click events. if the user clicks outside the menu
    // and the menu is open then toggle the display.
    const clickEventSub = this.eventListenerService.clickEvents$
      .pipe(
        filter(() => this.isActive),
        filter(target => !this.menu.nativeElement.contains(target))
      )
      .subscribe(() => {
        this.toggleMenu();
      });

    this.subscriptions.push(clickEventSub);

    this.subscriptions.push(this.onShowMenu?.subscribe(show => this.showMenu(show.x, show.y)));
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(sub => sub?.unsubscribe());
    this.destroy$.next();
    this.destroy$.complete();

    if (!!this.dropdownMenu.nativeElement && this.appendToBody) {
      if (this.dropdownMenu.nativeElement.parentElement === document.body) {
        document.body.removeChild(this.dropdownMenu.nativeElement);
      }
    }
  }

  hideAllBodyMenus(): void {
    Array.from(document.body.getElementsByClassName('dropdown-menu')).map(e => e.parentElement.classList.remove('is-active'));

    this.changeDetector.markForCheck();
  }

  toggleMenu(): void {
    const menuElm = this.menu.nativeElement;
    if (!menuElm) {
      return;
    }

    if (this.lastClickEvent > new Date().getTime() - 100) {
      return;
    }
    this.lastClickEvent = new Date().getTime();

    // set the position of the menu based on the element position;
    if (!this.dropdownMenu) {
      return;
    }

    const ddMenuElm = this.dropdownMenu.nativeElement;
    if (!ddMenuElm) {
      return;
    }

    if (this.isActive) {
      this.actionMenuService.actionMenu = null;
      this.actionMenuService.item = null;

      if (this.appendToBody) {
        ddMenuElm.classList.remove('is-active');
      } else {
        menuElm.classList.remove('is-active');
      }
    } else {
      this.actionMenuService.actionMenu = this;
      this.actionMenuService.item = this.item;

      if (this.appendToBody) {
        if (ddMenuElm.parentElement !== document.body) {
          ddMenuElm.parentElement.removeChild(ddMenuElm);
          document.body.appendChild(ddMenuElm);
        }
        ddMenuElm.classList.add('is-active');
      } else {
        menuElm.classList.add('is-active');
      }

      const { x, y } = this.getMenuPosition(menuElm, ddMenuElm);

      this.renderer.setStyle(this.dropdownMenu.nativeElement, 'top', `${y}px`);
      this.renderer.setStyle(this.dropdownMenu.nativeElement, 'left', `${x}px`);
    }

    this.isActive = !this.isActive;

    this.showHideMenu.emit(this.isActive);

    this.changeDetector.markForCheck();
  }

  hideMenu(): void {
    const menuElm = this.menu.nativeElement;
    if (!menuElm) {
      return;
    }

    if (this.lastClickEvent > new Date().getTime() - 100) {
      return;
    }
    this.lastClickEvent = new Date().getTime();

    // set the position of the menu based on the element position;
    if (!this.dropdownMenu) {
      return;
    }

    const ddMenuElm = this.dropdownMenu.nativeElement;
    if (!ddMenuElm) {
      return;
    }

    if (this.appendToBody) {
      ddMenuElm.classList.remove('is-active');
    } else {
      menuElm.classList.remove('is-active');
    }
    this.isActive = false;
  }

  showMenu(xPos: number, yPos: number): void {
    this.hideAllBodyMenus();

    this.actionMenuService.actionMenu = this;
    this.actionMenuService.item = this.item;

    const menuElm = this.menu.nativeElement;
    if (!menuElm) {
      return;
    }

    if (this.lastClickEvent > new Date().getTime() - 100) {
      return;
    }
    this.lastClickEvent = new Date().getTime();

    // set the position of the menu based on the element position;
    if (!this.dropdownMenu) {
      return;
    }

    const ddMenuElm = this.dropdownMenu.nativeElement;
    if (!ddMenuElm) {
      return;
    }

    if (this.isActive) {
      if (this.appendToBody) {
        ddMenuElm.classList.remove('is-active');
      } else {
        menuElm.classList.remove('is-active');
      }
    } else {
      if (this.appendToBody) {
        if (ddMenuElm.parentElement !== document.body) {
          ddMenuElm.parentElement.removeChild(ddMenuElm);
          document.body.appendChild(ddMenuElm);
        }
        ddMenuElm.classList.add('is-active');
      }
    }

    menuElm.classList.add('is-active');
    const { x, y } = this.getMenuPosition(menuElm, ddMenuElm, { x: xPos, y: yPos });

    this.renderer.setStyle(this.dropdownMenu.nativeElement, 'top', `${y}px`);
    this.renderer.setStyle(this.dropdownMenu.nativeElement, 'left', `${x}px`);

    this.isActive = !this.isActive;
  }

  getMenuPosition(elm, ddMenuElm, coordinates?: { x: number; y: number }): { x: number; y: number } {
    const { top, left, width, height } = elm.getBoundingClientRect();

    let verticalSizeOffset = this.size === ActionMenuSize.normal || this.size === ActionMenuSize.large ? 45 : 30;
    verticalSizeOffset = coordinates ? verticalSizeOffset : 0;

    const menuList = ddMenuElm.getBoundingClientRect();

    let y = top + window.scrollY + verticalSizeOffset;
    let x = left + window.scrollX;

    // When given specific coordinates, just render there
    if (coordinates) {
      y = coordinates.y;
      x = coordinates.x;
    }

    if (this.location === ActionMenuLocation.left) {
      x = x - (this.skinny ? 80 : menuList.width);
    }

    // Move the edge of the menu by the button width/height to allow more alignment options
    if (this.alignBottom) {
      y = y + height;
    }

    if (this.alignRight) {
      x = x + width;
    }

    // make sure we have enough room to show the menu below the
    // menu button. If not, then show it above the button
    const menuHeight = menuList.bottom - menuList.top;

    if (y + menuHeight > window.innerHeight) {
      y = top - menuHeight;
    }

    return { x, y };
  }
}
